<template>
  <section class="personality-page">
    <div class="personality-container">
      <header class="page-header">
        <div>
          <p class="eyebrow">Agent behavior</p>
          <h1>Personality & Behavior</h1>
          <p class="description">
            Define how your AI agent greets users, communicates, and reacts during conversations.
          </p>
        </div>

        <div class="status-panel">
          <div class="status-badge">
            Active agent
          </div>
          <div class="status-meta">
            <span>{{ languageLabel(selectedVoiceLanguage) }}</span>
            <strong>{{ visibleVoices.length }} voices visible</strong>
          </div>
        </div>
      </header>

      <div v-if="success" class="success-message">
        Personality settings saved successfully.
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <form class="settings-form" @submit.prevent="saveSettings">
        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="tone">Agent tone</label>
              <p>
                Choose the general tone your agent should use when speaking with users.
              </p>
            </div>
          </div>

          <select v-model="personality.tone" id="tone">
            <option value="friendly">Friendly</option>
            <option value="professional">Professional</option>
            <option value="warm">Warm</option>
            <option value="formal">Formal</option>
            <option value="neutral">Neutral</option>
            <option value="concise">Concise</option>
          </select>
        </div>

        <div class="setting-card voice-card">
          <div class="setting-header">
            <div class="voice-card-copy">
              <label>Voice</label>
              <p>
                Choose the spoken voice for this agent. Previews use ready-made recordings already available in the backend.
              </p>
            </div>

            <div class="voice-card-keyline">
              <span>Selected</span>
              <strong>{{ selectedVoice ? selectedVoice.label : 'Auto-selected' }}</strong>
            </div>
          </div>

          <div class="voice-toolbar">
            <div class="voice-toolbar-top">
              <div class="voice-language-select">
                <label for="voiceLanguage">Language</label>
                <select id="voiceLanguage" v-model="selectedVoiceLanguage" @change="onLanguageChange">
                  <option v-for="language in voiceLanguageOptions" :key="language.key" :value="language.key">
                    {{ language.label }}
                  </option>
                </select>
              </div>

              <div class="voice-search">
                <label for="voiceSearch">Search</label>
                <input
                  id="voiceSearch"
                  v-model.trim="voiceFilters.search"
                  type="search"
                  placeholder="Search by name, accent, description..."
                />
              </div>
            </div>

            <div class="voice-filters-grid">
              <div class="mini-field">
                <label for="voiceAccent">Accent</label>
                <select id="voiceAccent" v-model="voiceFilters.accent">
                  <option value="">All</option>
                  <option v-for="option in accentOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceLocale">Locale</label>
                <select id="voiceLocale" v-model="voiceFilters.locale">
                  <option value="">All</option>
                  <option v-for="option in localeOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceGender">Gender</label>
                <select id="voiceGender" v-model="voiceFilters.gender">
                  <option value="">All</option>
                  <option v-for="option in genderOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceCategory">Category</label>
                <select id="voiceCategory" v-model="voiceFilters.category">
                  <option value="">All</option>
                  <option v-for="option in categoryOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceAge">Age</label>
                <select id="voiceAge" v-model="voiceFilters.age">
                  <option value="">All</option>
                  <option v-for="option in ageOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceUseCase">Use case</label>
                <select id="voiceUseCase" v-model="voiceFilters.useCase">
                  <option value="">All</option>
                  <option v-for="option in useCaseOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceDescriptive">Descriptor</label>
                <select id="voiceDescriptive" v-model="voiceFilters.descriptive">
                  <option value="">All</option>
                  <option v-for="option in descriptiveOptions" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>

              <div class="mini-field">
                <label for="voiceSort">Sort</label>
                <select id="voiceSort" v-model="voiceFilters.sortBy">
                  <option value="recommended">Recommended</option>
                  <option value="latest">Latest</option>
                  <option value="label">Name</option>
                  <option value="notice">Notice period</option>
                  <option value="usage">Usage</option>
                </select>
              </div>

              <label class="mini-toggle">
                <input type="checkbox" v-model="voiceFilters.featuredOnly" />
                <span>Featured only</span>
              </label>

              <label class="mini-toggle">
                <input type="checkbox" v-model="voiceFilters.liveModerationOnly" />
                <span>Live moderation</span>
              </label>

              <label class="mini-toggle">
                <input type="checkbox" v-model="voiceFilters.freeUsersOnly" />
                <span>Free users allowed</span>
              </label>

              <button type="button" class="reset-voice-filters" @click="resetVoiceFilters">
                Reset filters
              </button>
            </div>

            <div class="voice-summary" v-if="selectedVoice">
              <div>
                <span>Selected voice</span>
                <strong>{{ selectedVoice.label }}</strong>
              </div>

              <div>
                <span>Language</span>
                <strong>{{ languageLabel(selectedVoice.language) }}</strong>
              </div>

              <div>
                <span>Visible voices</span>
                <strong>{{ visibleVoices.length }}</strong>
              </div>

              <div>
                <span>Loaded voices</span>
                <strong>{{ voices.length }}</strong>
              </div>
            </div>
          </div>

          <div v-if="visibleVoices.length" class="voice-list">
            <article
              v-for="voice in visibleVoices"
              :key="voice.id"
              class="voice-option"
              :class="{ selected: isVoiceSelected(voice) }"
            >
              <div class="voice-option-header">
                <div class="voice-copy">
                  <p class="voice-language">{{ languageLabel(voice.language) }}</p>
                  <h3>{{ voice.label }}</h3>
                  <p class="voice-description">{{ voice.description }}</p>
                </div>

                <div class="voice-actions">
                  <button
                    type="button"
                    class="preview-button"
                    @click="previewVoice(voice)"
                    :disabled="playingVoiceId === voice.id"
                  >
                    {{ playingVoiceId === voice.id ? 'Playing...' : 'Play' }}
                  </button>

                  <button
                    type="button"
                    class="select-button"
                    :class="{ selected: isVoiceSelected(voice) }"
                    @click="selectVoice(voice)"
                  >
                    {{ isVoiceSelected(voice) ? 'Selected' : 'Select' }}
                  </button>
                </div>
              </div>

              <div class="voice-badges">
                <span v-if="voice.language" class="voice-badge is-strong">{{ languageLabel(voice.language) }}</span>
                <span v-if="voice.featured" class="voice-badge">Featured</span>
                <span v-if="voice.live_moderation_enabled !== null && voice.live_moderation_enabled !== undefined" class="voice-badge">
                  Live moderation: {{ voice.live_moderation_enabled ? 'Yes' : 'No' }}
                </span>
                <span v-if="voice.free_users_allowed !== null && voice.free_users_allowed !== undefined" class="voice-badge">
                  Free users: {{ voice.free_users_allowed ? 'Yes' : 'No' }}
                </span>
                <span v-if="voice.notice_period !== null && voice.notice_period !== undefined" class="voice-badge">
                  Notice: {{ formatNoticePeriod(voice.notice_period) }}
                </span>
                <span v-if="voice.usage_character_count_1y !== null && voice.usage_character_count_1y !== undefined" class="voice-badge">
                  Usage 1y: {{ formatCount(voice.usage_character_count_1y) }} chars
                </span>
                <span v-if="voice.usage_character_count_7d !== null && voice.usage_character_count_7d !== undefined" class="voice-badge">
                  Usage 7d: {{ formatCount(voice.usage_character_count_7d) }} chars
                </span>
                <span v-if="voice.custom_rate !== null && voice.custom_rate !== undefined" class="voice-badge">
                  Credit multiplier: {{ formatMultiplier(voice.custom_rate) }}
                </span>
                <span v-if="voice.fiat_rate !== null && voice.fiat_rate !== undefined" class="voice-badge">
                  Cost estimate: {{ voice.fiat_rate }}
                </span>
                <span v-if="voice.locale" class="voice-badge">{{ voice.locale }}</span>
                <span v-if="voice.accent" class="voice-badge">{{ voice.accent }}</span>
                <span v-if="voice.gender" class="voice-badge">{{ voice.gender }}</span>
                <span v-if="voice.age" class="voice-badge">{{ voice.age }}</span>
                <span v-if="voice.category" class="voice-badge">{{ voice.category }}</span>
                <span v-if="voice.use_case" class="voice-badge">{{ voice.use_case }}</span>
                <span v-if="voice.descriptive" class="voice-badge">{{ voice.descriptive }}</span>
                <span v-if="voice.model_id" class="voice-badge">{{ voice.model_id }}</span>
                <span v-if="voice.cloned_by_count !== null && voice.cloned_by_count !== undefined" class="voice-badge">
                  Clones: {{ formatCount(voice.cloned_by_count) }}
                </span>
              </div>

              <div class="voice-wave" :class="{ active: playingVoiceId === voice.id }">
                <span v-for="bar in 7" :key="bar"></span>
              </div>
            </article>
          </div>

          <p v-else class="voice-empty">
            {{ loadingVoices ? 'Loading voice catalog...' : 'No voice recordings are available for this language yet.' }}
          </p>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="greeting">Custom greeting</label>
              <p>
                This is the opening message your agent can use when starting a conversation.
              </p>
            </div>
          </div>

          <textarea
            id="greeting"
            v-model="personality.greeting"
            rows="4"
            placeholder="Hello, thank you for contacting us. How can I help you today?"
          ></textarea>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="style">Response style</label>
              <p>
                Control whether the agent should keep replies short, detailed, formal, or conversational.
              </p>
            </div>
          </div>

          <select v-model="personality.style" id="style">
            <option value="short">Short</option>
            <option value="detailed">Detailed</option>
            <option value="formal">Formal</option>
            <option value="conversational">Conversational</option>
            <option value="direct">Direct</option>
          </select>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="reactions">Custom reactions</label>
              <p>
                Add specific instructions for how the agent should react to certain phrases, questions, or situations.
              </p>
            </div>
          </div>

          <textarea
            id="reactions"
            v-model="personality.reactions"
            rows="5"
            placeholder="Example: If the user asks for pricing, explain the available plans and offer to book a setup call."
          ></textarea>
        </div>

        <div class="setting-card">
          <div class="setting-header">
            <div>
              <label for="fallback">Fallback behavior</label>
              <p>
                Define what the agent should do when it does not know the answer.
              </p>
            </div>
          </div>

          <textarea
            id="fallback"
            v-model="personality.fallback"
            rows="4"
            placeholder="If you are unsure, ask one clarifying question or offer to route the request to a human."
          ></textarea>
        </div>

        <div class="toggle-grid">
          <div class="toggle-card">
            <div>
              <label for="oneQuestion">Ask one question at a time</label>
              <p>
                Helps keep the conversation clear and avoids overwhelming the user.
              </p>
            </div>

            <input
              id="oneQuestion"
              type="checkbox"
              v-model="personality.askOneQuestionAtATime"
            />
          </div>

          <div class="toggle-card">
            <div>
              <label for="allowSmallTalk">Allow small talk</label>
              <p>
                Lets the agent respond naturally to greetings and casual comments.
              </p>
            </div>

            <input
              id="allowSmallTalk"
              type="checkbox"
              v-model="personality.allowSmallTalk"
            />
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="secondary-button" @click="resetDefaults">
            Reset defaults
          </button>

          <button type="submit" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save settings' }}
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import Cookies from 'js-cookie'
import * as api from '@/services/api.js'

const DEFAULT_LANGUAGE = 'en'
const ALL_LANGUAGE_KEY = 'all'

const LANGUAGE_LABELS = {
  ar: 'Arabic',
  bg: 'Bulgarian',
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  fi: 'Finnish',
  fil: 'Filipino',
  fr: 'French',
  hr: 'Croatian',
  hu: 'Hungarian',
  hi: 'Hindi',
  id: 'Indonesian',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  ms: 'Malay',
  nl: 'Dutch',
  no: 'Norwegian',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  sk: 'Slovak',
  sv: 'Swedish',
  ta: 'Tamil',
  tr: 'Turkish',
  uk: 'Ukrainian',
  vi: 'Vietnamese',
  zh: 'Chinese'
}

const FALLBACK_LANGUAGE_CODES = [
  'en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'da', 'fi', 'cs',
  'tr', 'ru', 'ar', 'hi', 'ja', 'ko', 'zh', 'el', 'ro', 'bg', 'hr', 'ms',
  'sk', 'ta', 'uk', 'vi', 'fil', 'hu', 'no', 'id'
]

function normalizeLanguage(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return DEFAULT_LANGUAGE
  if (raw === ALL_LANGUAGE_KEY) return ALL_LANGUAGE_KEY
  if (['english', 'en'].includes(raw)) return 'en'
  if (['french', 'francais', 'français', 'fr'].includes(raw)) return 'fr'
  if (['spanish', 'español', 'es'].includes(raw)) return 'es'
  if (['portuguese', 'pt', 'português'].includes(raw)) return 'pt'
  if (['german', 'de'].includes(raw)) return 'de'
  if (['italian', 'it'].includes(raw)) return 'it'
  if (['chinese', 'zh', 'cn'].includes(raw)) return 'zh'
  return raw
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase()
}

function parseLanguageList(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.map(normalizeLanguage)

  const raw = String(value).trim()
  if (!raw) return []

  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.map(normalizeLanguage)
    } catch (err) {
      console.warn('Could not parse allowed_languages JSON:', err)
    }
  }

  return raw
    .split(/[,;\n|]/)
    .map((item) => normalizeLanguage(item))
    .filter(Boolean)
}

function uniqueLanguages(languages) {
  return [...new Set(languages.filter(Boolean))]
}

function normalizeFilterValue(value) {
  return String(value || '').trim().toLowerCase()
}

function parseVoiceBehavior(rawBehavior) {
  if (!rawBehavior) return {}
  if (typeof rawBehavior === 'object') return rawBehavior

  try {
    return JSON.parse(rawBehavior)
  } catch (err) {
    console.warn('Could not parse behavior JSON:', err)
    return {}
  }
}

function resolveVoiceId(voice) {
  return voice?.voice_key || voice?.provider_voice_id || voice?.id || ''
}

export default {
  name: 'PersonalityBehavior',

  data() {
    return {
      profileId: null,
      agentId: null,
      agentLanguage: DEFAULT_LANGUAGE,
      availableLanguages: [...FALLBACK_LANGUAGE_CODES],
      selectedVoiceLanguage: DEFAULT_LANGUAGE,
      voices: [],
      success: false,
      error: '',
      loadingVoices: false,
      saving: false,
      playingVoiceId: null,
      previewAudio: null,
      voiceFilters: {
        search: '',
        accent: '',
        locale: '',
        gender: '',
        category: '',
        age: '',
        useCase: '',
        descriptive: '',
        sortBy: 'recommended',
        featuredOnly: false,
        liveModerationOnly: false,
        freeUsersOnly: false
      },

      personality: {
        tone: 'professional',
        greeting: '',
        style: 'short',
        reactions: '',
        fallback: '',
        askOneQuestionAtATime: true,
        allowSmallTalk: true,
        voice: null
      }
    }
  },

  computed: {
    voiceLanguageOptions() {
      return [
        {
          key: ALL_LANGUAGE_KEY,
          label: 'All languages'
        },
        ...this.availableLanguages.map((key) => ({
          key,
          label: this.languageLabel(key)
        }))
      ].filter((option, index, list) => list.findIndex((item) => item.key === option.key) === index)
    },

    accentOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.accent))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    localeOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.locale))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    ageOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.age))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    useCaseOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.use_case))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    descriptiveOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.descriptive))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    genderOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.gender))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    categoryOptions() {
      return uniqueLanguages(
        this.voices
          .map((voice) => normalizeText(voice.category))
          .filter(Boolean)
      ).sort((a, b) => a.localeCompare(b))
    },

    visibleVoices() {
      const search = normalizeFilterValue(this.voiceFilters.search)
      const accent = normalizeFilterValue(this.voiceFilters.accent)
      const locale = normalizeFilterValue(this.voiceFilters.locale)
      const gender = normalizeFilterValue(this.voiceFilters.gender)
      const category = normalizeFilterValue(this.voiceFilters.category)
      const age = normalizeFilterValue(this.voiceFilters.age)
      const useCase = normalizeFilterValue(this.voiceFilters.useCase)
      const descriptive = normalizeFilterValue(this.voiceFilters.descriptive)

      const filtered = this.voices.filter((voice) => {
        const haystack = [
          voice.label,
          voice.description,
          voice.accent,
          voice.locale,
          voice.gender,
          voice.category,
          voice.age,
          voice.descriptive,
          voice.use_case,
          voice.language,
          voice.provider_voice_id,
          voice.voice_key,
          this.voiceText(voice, 'raw_json.notice_period'),
          this.voiceText(voice, 'raw_json.featured'),
          this.voiceText(voice, 'raw_json.model_id')
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        const matchesSearch = !search || haystack.includes(search)
        const matchesAccent = !accent || normalizeFilterValue(voice.accent) === accent
        const matchesLocale = !locale || normalizeFilterValue(voice.locale) === locale
        const matchesGender = !gender || normalizeFilterValue(voice.gender) === gender
        const matchesCategory = !category || normalizeFilterValue(voice.category) === category
        const matchesAge = !age || normalizeFilterValue(voice.age) === age
        const matchesUseCase = !useCase || normalizeFilterValue(voice.use_case) === useCase
        const matchesDescriptive =
          !descriptive || normalizeFilterValue(voice.descriptive) === descriptive
        const matchesFeatured = !this.voiceFilters.featuredOnly || this.voiceBool(voice, 'featured')
        const matchesLiveModeration =
          !this.voiceFilters.liveModerationOnly || this.voiceBool(voice, 'live_moderation_enabled')
        const matchesFreeUsers =
          !this.voiceFilters.freeUsersOnly || this.voiceBool(voice, 'free_users_allowed')

        return (
          matchesSearch &&
          matchesAccent &&
          matchesLocale &&
          matchesGender &&
          matchesCategory &&
          matchesAge &&
          matchesUseCase &&
          matchesDescriptive &&
          matchesFeatured &&
          matchesLiveModeration &&
          matchesFreeUsers
        )
      })

      const sorted = [...filtered]
      switch (this.voiceFilters.sortBy) {
        case 'latest':
          sorted.sort((a, b) => new Date(this.voiceDate(b)) - new Date(this.voiceDate(a)))
          break
        case 'label':
          sorted.sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')))
          break
        case 'notice':
          sorted.sort((a, b) => this.voiceNumber(a, 'notice_period') - this.voiceNumber(b, 'notice_period'))
          break
        case 'usage':
          sorted.sort((a, b) => this.voiceNumber(b, 'usage_character_count_1y') - this.voiceNumber(a, 'usage_character_count_1y'))
          break
        case 'recommended':
        default:
          sorted.sort((a, b) => {
            const featuredA = this.voiceBool(a, 'featured') ? 1 : 0
            const featuredB = this.voiceBool(b, 'featured') ? 1 : 0
            if (featuredA !== featuredB) return featuredB - featuredA
            const sortA = Number(a.sort_order || 0)
            const sortB = Number(b.sort_order || 0)
            if (sortA !== sortB) return sortA - sortB
            return String(a.label || '').localeCompare(String(b.label || ''))
          })
      }

      return sorted
    },

    selectedVoice() {
      return this.personality.voice || null
    }
  },

  async mounted() {
    const token = Cookies.get('token')

    if (!token) {
      return this.$router.push('/login')
    }

    const agentId = Cookies.get('active_agent_id')

    if (!agentId) {
      this.error = 'No active agent selected.'
      return
    }

    this.agentId = agentId

    try {
      const [profilesResult, agentResult, languagesResult] = await Promise.allSettled([
        api.personalityProfiles.getAll(agentId),
        api.agents.getById(agentId),
        api.voiceCatalog.getLanguages()
      ])

      const profiles =
        profilesResult.status === 'fulfilled' && Array.isArray(profilesResult.value)
          ? profilesResult.value
          : []
      const agent = agentResult.status === 'fulfilled' ? agentResult.value : null
      let catalogLanguages =
        languagesResult.status === 'fulfilled' && Array.isArray(languagesResult.value?.languages)
          ? languagesResult.value.languages.map((row) => normalizeLanguage(row.language))
          : []

      this.agentLanguage = normalizeLanguage(
        agent?.default_language || agent?.default_lang || DEFAULT_LANGUAGE
      )

      const agentLanguages = uniqueLanguages([
        this.agentLanguage,
        ...parseLanguageList(agent?.allowed_languages)
      ])

      const filteredLanguages = agentLanguages.filter((language) =>
        catalogLanguages.includes(language)
      )

      this.availableLanguages = uniqueLanguages([
        ...FALLBACK_LANGUAGE_CODES,
        ...catalogLanguages,
        ...filteredLanguages,
        ...agentLanguages,
        this.agentLanguage
      ])

      if (!catalogLanguages.length) {
        await this.syncVoiceCatalog([this.agentLanguage])
        catalogLanguages = [this.agentLanguage]
      }

      const warmupLanguages = uniqueLanguages([
        ...agentLanguages,
        this.agentLanguage
      ]).filter((language) => !catalogLanguages.includes(language))

      if (warmupLanguages.length) {
        await this.syncVoiceCatalog(warmupLanguages.slice(0, 4))
      }

      const profile = profiles[0]
      if (profile) {
        this.profileId = profile.id
      }

      const behavior = parseVoiceBehavior(profile?.behavior || '{}')
      const savedVoice = behavior.voice && typeof behavior.voice === 'object' ? behavior.voice : null
      const savedLanguage = normalizeLanguage(
        savedVoice?.language || this.agentLanguage || this.availableLanguages[0] || DEFAULT_LANGUAGE
      )

      this.selectedVoiceLanguage = this.availableLanguages.includes(savedLanguage)
        ? savedLanguage
        : this.availableLanguages[0] || this.agentLanguage

      this.personality = {
        tone: profile?.tone || 'professional',
        greeting: behavior.greeting || '',
        style: behavior.style || 'short',
        reactions: behavior.reactions || '',
        fallback: behavior.fallback || '',
        askOneQuestionAtATime:
          behavior.askOneQuestionAtATime !== undefined
            ? behavior.askOneQuestionAtATime
            : true,
        allowSmallTalk:
          behavior.allowSmallTalk !== undefined ? behavior.allowSmallTalk : true,
        voice: savedVoice
      }

      await this.loadVoices(this.selectedVoiceLanguage, savedVoice)
    } catch (err) {
      console.warn('Could not load personality profile:', err)
      this.error = 'Unable to load personality settings.'
    }
  },

  beforeUnmount() {
    this.stopPreview()
  },

  methods: {
    languageLabel(language) {
      const normalized = normalizeLanguage(language)
      return LANGUAGE_LABELS[normalized] || normalized.toUpperCase()
    },

    onLanguageChange() {
      this.loadVoices(this.selectedVoiceLanguage, null)
    },

    resetVoiceFilters() {
      this.voiceFilters = {
        search: '',
        accent: '',
        locale: '',
        gender: '',
        category: '',
        age: '',
        useCase: '',
        descriptive: '',
        sortBy: 'recommended',
        featuredOnly: false,
        liveModerationOnly: false,
        freeUsersOnly: false
      }
    },

    async loadVoices(language, preferredVoice = null, canSyncMissing = true) {
      this.loadingVoices = true
      this.error = ''
      const normalizedLanguage = normalizeLanguage(language)
      const isAllLanguages = normalizedLanguage === ALL_LANGUAGE_KEY
      const query = {
        search: this.voiceFilters.search || undefined,
        accent: this.voiceFilters.accent || undefined,
        locale: this.voiceFilters.locale || undefined,
        gender: this.voiceFilters.gender || undefined,
        age: this.voiceFilters.age || undefined,
        category: this.voiceFilters.category || undefined,
        use_case: this.voiceFilters.useCase || undefined,
        descriptive: this.voiceFilters.descriptive || undefined
      }

      try {
        const result = await api.voiceCatalog.getAll({
          language: isAllLanguages ? undefined : normalizedLanguage,
          limit: 200,
          page: 1,
          ...query
        })

        this.voices = Array.isArray(result?.items) ? result.items : []

        if (!this.voices.length) {
          if (canSyncMissing) {
            if (!isAllLanguages) {
              await this.syncVoiceCatalog([normalizedLanguage])
            }
            const retry = await api.voiceCatalog.getAll({
              language: isAllLanguages ? undefined : normalizedLanguage,
              limit: 200,
              page: 1,
              ...query
            })

            this.voices = Array.isArray(retry?.items) ? retry.items : []
          }

          if (!this.voices.length) {
            this.personality.voice = preferredVoice || null
            return
          }
        }

        const matchedVoice =
          this.findMatchingVoice(preferredVoice) || this.findRecommendedVoice(this.voices) || this.voices[0]

        this.personality.voice = matchedVoice
      } catch (err) {
        console.warn('Could not load voice catalog:', err)
        this.voices = []
      } finally {
        this.loadingVoices = false
      }
    },

    async syncVoiceCatalog(languages) {
      try {
        await api.voiceCatalog.sync({
          languages: Array.isArray(languages) ? languages : [],
          include_shared: true,
          include_private: false,
          max_per_language: 10,
          max_private_voices: 0
        })
      } catch (err) {
        console.warn('Voice catalog sync failed:', err)
      }
    },

    findMatchingVoice(target) {
      if (!target) return null
      const targetId = resolveVoiceId(target)

      return this.voices.find((voice) => {
        const voiceId = resolveVoiceId(voice)
        return (
          voiceId === targetId ||
          (target.provider_voice_id && voice.provider_voice_id === target.provider_voice_id) ||
          (target.voice_key && voice.voice_key === target.voice_key)
        )
      }) || null
    },

    findRecommendedVoice(voices) {
      return voices.find((voice) => Number(voice.sort_order) === 0) || voices[0] || null
    },

    voiceText(voice, keyPath) {
      if (!voice) return ''
      const parts = String(keyPath || '').split('.')
      let current = voice

      for (const part of parts) {
        if (current == null) return ''
        current = current[part]
      }

      return current == null ? '' : String(current)
    },

    voiceRaw(voice) {
      return voice?.raw_json && typeof voice.raw_json === 'object' ? voice.raw_json : {}
    },

    voiceBool(voice, key) {
      const raw = this.voiceRaw(voice)
      const value = voice?.[key] ?? raw?.[key]
      if (value === true || value === 1 || value === '1') return true
      if (typeof value === 'string') {
        return ['true', 'yes', 'y'].includes(value.toLowerCase())
      }
      return false
    },

    voiceNumber(voice, key) {
      const raw = this.voiceRaw(voice)
      const value = voice?.[key] ?? raw?.[key]
      const num = Number(value)
      return Number.isFinite(num) ? num : 0
    },

    voiceDate(voice) {
      return voice?.last_synced_at || voice?.created_at || voice?.updated_at || 0
    },

    formatCount(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '0'
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
      if (num >= 1000) return `${(num / 1000).toFixed(1)}k`
      return String(num)
    },

    formatMultiplier(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) return 'n/a'
      if (num === 1) return '1x'
      return `${Number.isInteger(num) ? num : num.toFixed(2)}x`
    },

    formatNoticePeriod(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) return '0d'
      return `${num}d`
    },

    async selectVoiceLanguage(language) {
      const normalized = normalizeLanguage(language)
      this.selectedVoiceLanguage = normalized
      await this.loadVoices(normalized, null)
    },

    isVoiceSelected(voice) {
      const current = this.personality.voice
      if (!current) return false

      const currentId = resolveVoiceId(current)
      const voiceId = resolveVoiceId(voice)

      return currentId === voiceId
    },

    selectVoice(voice) {
      this.personality.voice = { ...voice }
      this.selectedVoiceLanguage = normalizeLanguage(voice.language || this.selectedVoiceLanguage)
    },

    previewVoice(voice) {
      this.stopPreview()

      if (!voice?.sample_url) {
        return
      }

      const audio = new Audio(voice.sample_url)
      this.previewAudio = audio
      this.playingVoiceId = resolveVoiceId(voice)

      audio.onended = () => {
        if (this.previewAudio === audio) {
          this.playingVoiceId = null
          this.previewAudio = null
        }
      }

      audio.onerror = () => {
        if (this.previewAudio === audio) {
          this.playingVoiceId = null
          this.previewAudio = null
        }
      }

      audio.play().catch((err) => {
        console.warn('Voice preview playback failed:', err)
        this.playingVoiceId = null
        this.previewAudio = null
      })
    },

    stopPreview() {
      if (this.previewAudio) {
        this.previewAudio.pause()
        this.previewAudio = null
      }

      this.playingVoiceId = null
    },

    buildVoicePayload() {
      if (!this.personality.voice) {
        return null
      }

      return {
        id: this.personality.voice.id || null,
        voice_key: this.personality.voice.voice_key || null,
        label: this.personality.voice.label || null,
        language: this.personality.voice.language || null,
        locale: this.personality.voice.locale || null,
        accent: this.personality.voice.accent || null,
        gender: this.personality.voice.gender || null,
        age: this.personality.voice.age || null,
        category: this.personality.voice.category || null,
        descriptive: this.personality.voice.descriptive || null,
        use_case: this.personality.voice.use_case || null,
        description: this.personality.voice.description || null,
        provider_name: this.personality.voice.provider_name || null,
        provider_voice_id: this.personality.voice.provider_voice_id || null,
        sample_url: this.personality.voice.sample_url || null,
        sample_file: this.personality.voice.sample_file || null
      }
    },

    async saveSettings() {
      const agentId = this.agentId || Cookies.get('active_agent_id')

      if (!agentId) {
        this.error = 'No active agent selected.'
        return
      }

      this.saving = true
      this.success = false
      this.error = ''

      const behavior = {
        greeting: this.personality.greeting,
        style: this.personality.style,
        reactions: this.personality.reactions,
        fallback: this.personality.fallback,
        askOneQuestionAtATime: this.personality.askOneQuestionAtATime,
        allowSmallTalk: this.personality.allowSmallTalk,
        voice: this.buildVoicePayload()
      }

      const payload = {
        agent_id: agentId,
        tone: this.personality.tone,
        behavior
      }

      try {
        if (this.profileId) {
          await api.personalityProfiles.update(this.profileId, {
            ...payload,
            behavior: JSON.stringify(payload.behavior)
          })
        } else {
          const created = await api.personalityProfiles.create({
            ...payload,
            behavior: JSON.stringify(payload.behavior)
          })

          this.profileId = created.id
        }

        this.success = true
        setTimeout(() => {
          this.success = false
        }, 3000)
      } catch (err) {
        console.error('Personality save error:', err)
        this.error = 'Unable to save personality settings. Please try again.'
      } finally {
        this.saving = false
      }
    },

    resetDefaults() {
      this.selectedVoiceLanguage = this.availableLanguages[0] || this.agentLanguage || DEFAULT_LANGUAGE

      this.personality = {
        tone: 'professional',
        greeting: '',
        style: 'short',
        reactions: '',
        fallback: '',
        askOneQuestionAtATime: true,
        allowSmallTalk: true,
        voice: this.findRecommendedVoice(this.voices)
      }

      this.success = false
      this.error = ''
    }
  }
}
</script>

<style scoped>
.personality-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(99, 102, 241, 0.10), transparent 28%),
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.08), transparent 24%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 56px 20px 64px;
  color: #0f172a;
}

.personality-container {
  max-width: 1180px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 28px;
  align-items: flex-end;
  margin-bottom: 28px;
}

.eyebrow {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}

h1 {
  font-size: clamp(2.4rem, 3vw, 3.3rem);
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #020617;
  margin-bottom: 10px;
}

.description {
  max-width: 680px;
  color: #64748b;
  line-height: 1.7;
}

.status-badge {
  background: linear-gradient(135deg, #020617 0%, #111827 100%);
  color: white;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.82rem;
  font-weight: 900;
  white-space: nowrap;
  box-shadow: 0 16px 32px rgba(2, 6, 23, 0.18);
}

.status-panel {
  display: grid;
  gap: 10px;
  justify-items: end;
}

.status-meta {
  display: grid;
  justify-items: end;
  gap: 4px;
}

.status-meta span {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
}

.status-meta strong {
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 900;
}

.success-message,
.error-message {
  border-radius: 16px;
  padding: 14px 16px;
  font-weight: 700;
  font-size: 0.92rem;
  margin-bottom: 18px;
}

.success-message {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.settings-form {
  display: grid;
  gap: 18px;
}

.setting-card,
.toggle-card {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.94) 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 28px;
  padding: 26px;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(10px);
}

.setting-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.setting-header label,
.toggle-card label {
  display: block;
  font-size: 1.02rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.setting-header p,
.toggle-card p {
  color: #64748b;
  line-height: 1.6;
  font-size: 0.92rem;
  margin: 0;
}

select,
textarea {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 0.95rem;
  color: #0f172a;
  background: white;
  outline: none;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

select:focus,
textarea:focus {
  border-color: #020617;
  box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.06);
}

textarea {
  resize: vertical;
  line-height: 1.6;
  min-height: 110px;
}

.voice-card {
  gap: 18px;
}

.voice-card-copy {
  max-width: 700px;
}

.voice-card-keyline {
  min-width: 220px;
  padding: 14px 16px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0f172a 0%, #020617 100%);
  color: #fff;
  display: grid;
  gap: 6px;
  box-shadow: 0 18px 40px rgba(2, 6, 23, 0.15);
}

.voice-card-keyline span {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.7rem;
  font-weight: 900;
  color: rgba(255,255,255,0.68);
}

.voice-card-keyline strong {
  font-size: 1rem;
  font-weight: 900;
}

.voice-toolbar {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.9) 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.7);
}

.voice-toolbar-top {
  display: grid;
  grid-template-columns: minmax(190px, 230px) minmax(0, 1fr);
  gap: 14px;
  align-items: end;
}

.voice-language-select,
.voice-search,
.mini-field {
  display: grid;
  gap: 6px;
}

.voice-language-select label,
.voice-search label,
.mini-field label {
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.voice-filters-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding-top: 2px;
}

.voice-language-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.language-pill {
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  background: #f8fafc;
  color: #0f172a;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.language-pill.active {
  background: #020617;
  border-color: #020617;
  color: #fff;
}

.voice-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 0;
  border: 0;
  background: transparent;
}

.voice-summary span {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.voice-summary strong {
  color: #020617;
  font-size: 0.98rem;
}

.voice-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.voice-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 7px 10px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.76rem;
  font-weight: 800;
}

.voice-badge.is-strong {
  background: #0f172a;
  color: #fff;
}

.mini-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 14px;
  padding: 10px 12px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 700;
  white-space: nowrap;
}

.mini-toggle span {
  line-height: 1.2;
}

.reset-voice-filters {
  align-self: end;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 14px;
  padding: 10px 12px;
  background: #fff;
  color: #0f172a;
  font-weight: 900;
  cursor: pointer;
}

.voice-list {
  display: grid;
  gap: 14px;
  max-height: 640px;
  overflow-y: auto;
  padding-right: 6px;
}

.voice-option {
  display: grid;
  gap: 12px;
  border-radius: 22px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  padding: 18px 18px 16px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.voice-option.selected {
  border-color: rgba(2, 6, 23, 0.35);
  box-shadow: 0 22px 44px rgba(15, 23, 42, 0.10);
  transform: translateY(-2px);
}

.voice-option-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.voice-copy {
  min-width: 0;
}

.voice-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.voice-option h3 {
  margin: 4px 0 8px;
  color: #020617;
  font-size: 1.02rem;
  font-weight: 900;
}

.voice-language {
  margin: 0;
  color: #6366f1;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.voice-description {
  margin: 0;
  color: #64748b;
  line-height: 1.55;
  font-size: 0.9rem;
}

.preview-button,
.select-button {
  border: none;
  border-radius: 999px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.preview-button {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 10px 14px;
  white-space: nowrap;
}

.select-button {
  background: #f8fafc;
  color: #0f172a;
  padding: 10px 14px;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.select-button.selected {
  background: linear-gradient(135deg, #020617 0%, #111827 100%);
  color: #fff;
  box-shadow: 0 12px 26px rgba(2, 6, 23, 0.18);
}

.voice-wave {
  display: flex;
  align-items: end;
  gap: 6px;
  min-height: 28px;
}

.voice-wave span {
  width: 8px;
  height: 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.14);
}

.voice-wave.active span {
  background: #6366f1;
  animation: voicePulse 1s ease-in-out infinite;
}

.voice-wave.active span:nth-child(2n) {
  animation-delay: 0.08s;
}

.voice-wave.active span:nth-child(3n) {
  animation-delay: 0.16s;
}

.voice-empty {
  margin: 0;
  padding: 14px 16px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  color: #64748b;
}

.toggle-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.toggle-card {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
}

.toggle-card input[type='checkbox'] {
  width: 46px;
  height: 26px;
  accent-color: #020617;
  cursor: pointer;
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.form-actions button {
  border: none;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease, background 0.2s ease;
}

.form-actions button[type='submit'] {
  background: #020617;
  color: white;
}

.form-actions button[type='submit']:hover {
  transform: scale(1.02);
  background: #111827;
}

.form-actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.secondary-button {
  background: white;
  color: #020617;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
}

.secondary-button:hover {
  background: #f1f5f9;
  transform: scale(1.02);
}

@keyframes voicePulse {
  0%,
  100% {
    height: 10px;
    opacity: 0.45;
  }

  50% {
    height: 26px;
    opacity: 1;
  }
}

@media (max-width: 760px) {
  .personality-page {
    padding: 24px 14px 36px;
  }

  .page-header,
  .setting-header,
  .toggle-card,
  .form-actions {
    flex-direction: column;
  }

  .toggle-grid {
    grid-template-columns: 1fr;
  }

  .voice-option-header {
    flex-direction: column;
  }

  .voice-toolbar-top,
  .voice-filters-grid {
    grid-template-columns: 1fr;
  }

  .status-panel {
    justify-items: start;
  }

  .status-meta {
    justify-items: start;
  }

  .voice-card-keyline {
    width: 100%;
    min-width: 0;
  }

  .mini-toggle,
  .reset-voice-filters {
    width: 100%;
    justify-content: center;
  }

  h1 {
    font-size: 2rem;
  }

  .voice-actions {
    width: 100%;
  }

  .preview-button,
  .select-button {
    flex: 1;
  }

  .status-badge {
    width: fit-content;
  }

  .form-actions button {
    width: 100%;
  }
}
</style>
